import React from 'react';
import { Shield, Radar, Building2, CheckCircle2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import Seo from '@/components/Seo';
import { Button } from '@/components/ui/button';

const bundles = [
  {
    title: 'Reputation & Trust Governance Suite',
    description:
      'A governance framework to manage public reputation signals systematically—not reactively—so leadership keeps control of brand trust.',
    points: [
      'Public review management: consulting, responses, cleanup, and disputes',
      'Complaint and escalation pattern analysis',
      'Quarterly reputation & trust leadership reviews',
      'Defined oversight and reporting cadence'
    ]
  },
  {
    title: 'AI Trust, Visibility & Intelligence Layer',
    description:
      'Ensure your organisation is accurately represented across AI systems and digital discovery channels with verified, structured information.',
    points: [
      'AI Trust Registry with verified organisational facts',
      'AI Mirror Site for authoritative AI reference',
      'AI answer consistency audits',
      'AI & brand monitoring with leadership summaries'
    ]
  },
  {
    title: 'Internal Trust, Risk Audits & Early‑Warning Intelligence',
    description:
      'Surface internal risks early through structured feedback systems, enabling leadership to act before issues become public.',
    points: [
      'Confidential internal feedback loops for teams',
      'Risk audits across departments and business units',
      'Early‑warning signals and escalation guidance',
      'Executive‑ready reporting and remediation plans'
    ]
  }
];

const outcomes = [
  'Enterprise‑grade oversight of reputation and trust signals',
  'Accurate representation across AI search and chat platforms',
  'Early detection of risks before they become public issues',
  'Clear accountability across functions and business units',
  'Leadership visibility without day‑to‑day firefighting'
];

const enterpriseChallenges = [
  {
    title: 'Multi‑location complexity',
    description: 'Inconsistent reviews, profiles, and local signals create fragmented trust.'
  },
  {
    title: 'Reputation volatility',
    description: 'Small issues become high‑visibility events without early warning systems.'
  },
  {
    title: 'AI visibility risk',
    description: 'AI answers can amplify outdated or incorrect information at scale.'
  },
  {
    title: 'Governance gaps',
    description: 'Ownership is distributed across functions, delaying response and accountability.'
  }
];

const engagementSteps = [
  {
    title: 'Discovery & audit',
    description: 'We assess reputation signals, AI representations, and internal trust risk.'
  },
  {
    title: 'Governance design',
    description: 'Define ownership, escalation paths, and reporting cadence.'
  },
  {
    title: 'Implementation',
    description: 'Deploy monitoring, response workflows, and AI trust assets.'
  },
  {
    title: 'Leadership reporting',
    description: 'Executive dashboards with monthly/quarterly insights and actions.'
  }
];

const enterpriseAssurance = [
  'Defined SLAs for response and escalation handling',
  'Role‑based access and audit trails',
  'Privacy‑aware data handling and minimal data retention',
  'Dedicated enterprise point of contact'
];

export default function EnterprisePage() {
  return (
    <Layout>
      <Seo
        title="Enterprise Reputation & Trust Governance"
        description="Reduce public risk, protect brand trust, and ensure accurate AI visibility with enterprise governance."
        path="/enterprise"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-ybro-light/60 to-white py-24 lg:py-32">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ybro-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
              Enterprise Focus
            </span>
            <h1 className="text-4xl lg:text-5xl font-black font-heading text-slate-900 mb-6 leading-tight">
              Enterprise Reputation & Trust Governance
            </h1>
            <p className="text-lg text-slate-600 font-body leading-relaxed">
              Reduce public risk, protect brand trust, and ensure accurate AI visibility
              with a structured, enterprise‑grade governance approach.
            </p>
            <div className="mt-8">
              <Button asChild className="bg-ybro-primary hover:bg-ybro-dark text-white">
                <a href="/contact">Book an Enterprise Governance Review</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why this matters */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <Building2 className="w-8 h-8 text-ybro-primary mb-4" />
            <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">Brand risk is now public</h3>
            <p className="text-slate-600 font-body">
              Reviews, forums, and AI answers shape perception before leadership is aware.
              We help you govern these signals proactively.
            </p>
          </div>
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <Radar className="w-8 h-8 text-ybro-primary mb-4" />
            <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">Early‑warning intelligence</h3>
            <p className="text-slate-600 font-body">
              Structured internal feedback identifies issues early, reducing the risk of
              escalations and media exposure.
            </p>
          </div>
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <Shield className="w-8 h-8 text-ybro-primary mb-4" />
            <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">AI accuracy and trust</h3>
            <p className="text-slate-600 font-body">
              Applicants and stakeholders increasingly rely on AI summaries. We ensure
              those answers are accurate and up‑to‑date.
            </p>
          </div>
        </div>
      </section>

      {/* Enterprise challenges */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
              Enterprise Challenges
            </span>
            <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900">
              What enterprise teams are facing now
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {enterpriseChallenges.map((item) => (
              <div key={item.title} className="p-6 rounded-2xl border border-slate-200 bg-white">
                <h3 className="text-lg font-bold font-heading text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 font-body">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundles */}
      <section className="py-24 bg-ybro-light/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
              Enterprise Service Bundles
            </span>
            <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900">
              Three structured bundles to reduce risk and increase trust
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {bundles.map((bundle) => (
              <div key={bundle.title} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-3">{bundle.title}</h3>
                <p className="text-slate-600 font-body mb-4">{bundle.description}</p>
                <ul className="space-y-3">
                  {bundle.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-slate-600 font-body">
                      <CheckCircle2 className="w-4 h-4 text-ybro-primary mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement model */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
              Engagement Model
            </span>
            <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900">
              A clear enterprise delivery cadence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementSteps.map((step, index) => (
              <div key={step.title} className="p-6 rounded-2xl border border-slate-200 bg-white">
                <div className="text-sm font-bold text-ybro-primary mb-2">Step {index + 1}</div>
                <h3 className="text-lg font-bold font-heading text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 font-body text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assurance */}
      <section className="py-24 bg-ybro-light/30">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
              Enterprise Assurance
            </span>
            <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900">
              Built for enterprise governance standards
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {enterpriseAssurance.map((item) => (
              <div key={item} className="flex items-start gap-3 p-5 rounded-xl bg-white border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-ybro-primary mt-0.5" />
                <span className="text-slate-700 font-body">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900 mb-8 text-center">
            Outcomes leadership can expect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {outcomes.map((item) => (
              <div key={item} className="flex items-start gap-3 p-5 rounded-xl bg-ybro-light/40 border border-ybro-light">
                <CheckCircle2 className="w-5 h-5 text-ybro-primary mt-0.5" />
                <span className="text-slate-700 font-body">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ybro-light/30">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900 mb-6">
            Ready for an enterprise‑grade reputation programme?
          </h2>
          <p className="text-lg text-slate-600 mb-10 font-body">
            We’ll tailor the scope around your governance priorities, operational footprint, and risk profile.
          </p>
          <Button asChild className="bg-ybro-primary hover:bg-ybro-dark text-white">
            <a href="/contact">Talk to Enterprise Team</a>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
